<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Services",
};
</script>

<script setup lang="ts">
import ServicesTable from "@/components/models/service/ServicesTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import ServiceService, { type TService } from "@/api/service";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import type { PaginatedType, VFormFieldChoice } from "@/utils/DjangoTypes";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";
import { useAbility } from "@casl/vue";

definePage({
  meta: {
    title: "Services",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.SERVICE);
    },
  },
});

const snackStore = useSnackStore();
const serviceFetchEnabled = ref(false);

const serviceTypeChoices = ref<VFormFieldChoice<any>[]>([]);
const dealersChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  services: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: services,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["services"],
  queryFn: async (): Promise<PaginatedType<TService[]> | undefined> => {
    try {
      const [services, serviceTypesSelectable] = await Promise.all([
        ServiceService.service.getAll(queriesTableParams.services),
        ServiceService.serviceType.getAllSelectable(),
      ]);

      serviceTypeChoices.value = serviceTypesSelectable;

      return services.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => serviceFetchEnabled.value,
});

onMounted(async () => {
  serviceFetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <services-table
        :query-key="'services'"
        :data="services"
        :service-types="serviceTypeChoices"
        :loading="isPending || isFetching"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
